/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import msdyn365Commerce, {getCatalogId} from '@msdyn365-commerce/core';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IProductComparisonPreviewViewProps, ProductComparisonPreview } from '@msdyn365-commerce-modules/product-comparison';
import {ArrayExtensions, ObjectExtensions} from "@msdyn365-commerce-modules/retail-actions";
import {ProductPrice, ProductsDataActions} from "@msdyn365-commerce/retail-proxy";
import {PriceComponent} from "@msdyn365-commerce/components";
import {useEffect, useState} from "react";
import {ProductBrand} from "./product-comparison.view";
import {SimpleProduct} from "@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g";

export interface IProductRetailerExclusive {
    AttributesValue: boolean | undefined;
    ProductId: number | undefined;
}

export interface IProductRetiredExclusive {
    AttributesValue: boolean | undefined;
    ProductId: number | undefined;
}

/**
 * Represents product comparison preview view.
 * @param props - View props for product comparison preview.
 * @returns Functional component of product comparison preview.
 */
export const ProductComparisonPreviewViewFunctionalComponent: React.FC<IProductComparisonPreviewViewProps> = props => {
    if (!props.isMainContentReady) {
        return null;
    }

    const shouldBeExpanded = props.isExpanded ?? props.hasUserInteractedWithComparison;

    const containerClassName = classnames(
        ProductComparisonPreview.mainContainerClassName,
        shouldBeExpanded ? '' : `${ProductComparisonPreview.mainContainerClassName}__collapsed`
    );

    /**
     * Renders dynamic brand name.
     * @param brandNameClassName.
     * @param productId - Prod id.
     * @returns List of react nodes.
     */
    const renderDynamicBrandName = (
        brandNameClassName: string,
        productId?: number
    ) => {
        if (!ObjectExtensions.isNullOrUndefined(productsBrand) && productId) {
            const productsBrandList = Object.keys(productsBrand).map(function(productIdIndex){
                let brand = productsBrand[productIdIndex];
                return brand;
            });
            const brandList = productsBrandList.filter((item: ProductBrand, index) => item.ProductId === productId);
            return brandList.map((brandItem) => {
                return (<Node className={brandNameClassName} tag='div' key={productId}>{brandItem.AttributesValue}</Node>);
            });

        } else {
            return null;
        }
    };

    /**
     * Renders dynamic price.
     * @param priceNameClassName.
     * @param productId - Prod id.
     * @returns List of react nodes.
     */
    const renderDynamicPrice = (
        priceNameClassName: string,
        productId?: number
    ) => {
        if (!ObjectExtensions.isNullOrUndefined(productsPrice) && productId) {
            const productsPriceList = Object.keys(productsPrice).map(function(productIdIndex){
                let price = productsPrice[productIdIndex];
                return price;
            });
            const priceList = productsPriceList.filter((item: ProductPrice, index) => item.ProductId === productId);

            const productRetailerExclusiveList = Object.keys(productsRetailerExclusive).map(function(productIdIndex){
                return productsRetailerExclusive[productIdIndex];
            });
            const hasRetailer = productRetailerExclusiveList.filter((item: IProductRetailerExclusive, index) =>
                item.ProductId === productId && item.AttributesValue === true);

            const productRetiredExclusive = Object.keys(productsRetiredExclusive).map(function(productIdIndex){
                return productsRetiredExclusive[productIdIndex];
            });
            const hasRetired = productRetiredExclusive.filter((item: IProductRetiredExclusive, index) =>
                    item.ProductId === productId && item.AttributesValue === true);

            const isLoading = productsLoading.filter((item, index) => {
                return item === productId;
            });

            if (!ArrayExtensions.hasElements(hasRetailer) && !ArrayExtensions.hasElements(hasRetired)) {
                return priceList.map((priceItem) => {
                    const price: ProductPrice = {
                        BasePrice: priceItem.BasePrice,
                        AdjustedPrice: priceItem.AdjustedPrice,
                        CustomerContextualPrice: priceItem.CustomerContextualPrice,
                        TradeAgreementPrice: priceItem.TradeAgreementPrice,
                        MaxVariantPrice: priceItem.MaxVariantPrice,
                        MinVariantPrice: priceItem.MinVariantPrice,
                        DiscountAmount: priceItem.DiscountAmount,
                        CurrencyCode: priceItem.CurrencyCode,
                        DiscountLines: priceItem.DiscountLines,
                        SalesAgreementPrice: priceItem.SalesAgreementPrice,
                        PriceSourceTypeValue: priceItem.PriceSourceTypeValue
                    };

                    return !ArrayExtensions.hasElements(isLoading) && (<Node className={priceNameClassName} tag='div'  key={productId}>
                        <PriceComponent
                            context={props.context}
                            id={props.id + 'product-comparision'}
                            typeName={props.typeName}
                            data={{ price:price }}
                            // savingsText={savingsText}
                            // freePriceText={'freePriceText'}
                            freePriceText={'FREE'}
                            // originalPriceText={originalPriceText}
                            originalPriceText={'Original price'}
                            // isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                            // priceResources={priceResources}
                        />
                    </Node>);
                });
            } else {
                return (<Node className={priceNameClassName} tag='div'  key={productId}></Node>);
            }

        } else {
            return (<Node className={priceNameClassName} tag='div'  key={productId}></Node>);
        }
    };


    // TODO: Move this function as common part
    //  see src/themes/britax-adventureworks/views/components/product.component.tsx.
    const _getActiveProductAsync = async (product?: SimpleProduct): Promise<ProductPrice | undefined> => {
        const staticArray: number[] = [product?.RecordId?? 0];
        const catalogId=getCatalogId(props.context.request);

        try {
            const attributeValues = await ProductsDataActions.getActivePricesAsync(
                { callerContext: props.context.actionContext, bypassCache: 'get' },
                {ChannelId:props.context.request.apiSettings.channelId,CatalogId:catalogId},
                staticArray ?? [],
                new Date(),
                null,
                null,
                true

            );
            return ArrayExtensions.firstOrDefault(attributeValues);
        } catch (error) {
            console.error('Error fetching catalog product attributes:', error);
            return undefined;
        }
    };

    const _getCatalogProductAttributesAsync = async (product?: SimpleProduct): Promise<ProductBrand | undefined> => {
        try {
            const attributeValues = await ProductsDataActions.getAttributeValuesAsync(
                { callerContext: props.context.actionContext, bypassCache: 'get' },
                product?.RecordId?? 0,
                props.context.request.apiSettings.channelId,
                getCatalogId(props.context.request)
            );

            const retiredProductsLabelString = 'Retired Products (D2C)';
            const retailerExclusiveLabelString = 'Retailer Exclusive (D2C)';
            const NZRetiredProductsLabelString = 'BNZ Retired Products (D2C)';
            const NZRetailerExclusiveLabelString = 'BNZ Retailer Exclusive (D2C)';
            const searchProductAttrs = [retailerExclusiveLabelString, retiredProductsLabelString,NZRetiredProductsLabelString,NZRetailerExclusiveLabelString];
            const productAttributes = attributeValues?.filter(
                value =>
                    value.Name !== null &&
                    value.Name !== undefined &&
                    searchProductAttrs.some(label => value.Name?.includes(label) && value.BooleanValue === true)
            ).map(value => value.Name);

            const retailerExclusive = productAttributes?.some(
                (name: string | undefined) =>
                    name?.toLowerCase() === retailerExclusiveLabelString.toLowerCase() ||
                    name?.toLowerCase() === NZRetailerExclusiveLabelString.toLowerCase()
            );
            const retiredProducts = productAttributes?.some(
                (name: string | undefined) =>
                    name?.toLowerCase() === retiredProductsLabelString.toLowerCase() || name?.toLowerCase() === NZRetiredProductsLabelString.toLowerCase()
            );
            setProductRetiredExclusive({AttributesValue: retiredProducts, ProductId: product?.RecordId});
            setProductRetailerExclusive({AttributesValue: retailerExclusive, ProductId: product?.RecordId});

            const searchProductAttr: string[] = ['Brands'];

            const productAttributesValue = attributeValues
                ?.filter(
                    value => value.Name !== null && value.Name !== undefined && searchProductAttr.some(attr => value.Name?.includes(attr))
                )
                .map(value => value.TextValue);

            const combinedAttributeValues = productAttributesValue?.join(', ');


            return {
                AttributesValue: combinedAttributeValues,
                ProductId: product?.RecordId
            };
        } catch (error) {
            console.error('Error fetching catalog product attributes:', error);
            return {
                AttributesValue: undefined,
                ProductId: product?.RecordId
            };
        }

    };

    const [productsPrice, setProductsPrice] = useState<ProductPrice[]>([]);
    const [productPrice, setProductPrice] = useState<ProductPrice | undefined>();

    const [productsBrand, setProductsBrand] = useState<ProductBrand[]>([]);
    const [productBrand, setProductBrand] = useState<ProductBrand | undefined>();

    const [productsRetailerExclusive, setProductsRetailerExclusive] = useState<IProductRetailerExclusive[]>([]);
    const [productRetailerExclusive, setProductRetailerExclusive] = useState<IProductRetailerExclusive | undefined>();

    const [productsRetiredExclusive, setProductsRetiredExclusive] = useState<IProductRetiredExclusive[]>([]);
    const [productRetiredExclusive, setProductRetiredExclusive] = useState<IProductRetiredExclusive | undefined>();
    const [productsLoading, setProductsLoading] = useState<number[]>([]);

    useEffect(() => {
        const fetchProductsAttributes = async () => {
            props.productsMainInfo?.map(async (info, index) => {
                // Is fetching product data.
                if (info.productData.product?.RecordId) {
                    productsLoading[index] = info.productData.product?.RecordId;
                    setProductsLoading(productsLoading);
                }
                const activePrice = await _getActiveProductAsync(info.productData.product);
                setProductPrice(activePrice);
                const productAttribute = await _getCatalogProductAttributesAsync(info.productData.product);
                setProductBrand(productAttribute);
            });
        };
        if (props.isMainContentReady) {
            fetchProductsAttributes();
        }
    }, [props.productsMainInfo]);

    useEffect(() => {
        if (props.isMainContentReady && productPrice?.ProductId) {
            let price = [];
            price[productPrice?.ProductId] = productPrice;
            setProductsPrice({...productsPrice, ...price})
        }

    }, [productPrice]);

    useEffect(() => {
        if (props.isMainContentReady && productBrand?.ProductId) {
            let brand = [];
            brand[productBrand?.ProductId] = productBrand;
            setProductsBrand({...productsBrand, ...brand})
        }

    }, [productBrand]);

    useEffect(() => {
        if (props.isMainContentReady && productRetiredExclusive?.ProductId) {
            let data = [];
            data[productRetiredExclusive?.ProductId] = productRetiredExclusive;
            setProductsRetiredExclusive({...productsRetiredExclusive, ...data})
            // Done fetching product data.
            const productsLoadingUpdate = productsLoading.filter((item, index) => {
                return item !== productRetiredExclusive?.ProductId;
            });
            setProductsLoading(productsLoadingUpdate);
        }

    }, [productRetiredExclusive]);

    useEffect(() => {
        if (props.isMainContentReady && productRetailerExclusive?.ProductId) {
            let data = [];
            data[productRetailerExclusive?.ProductId] = productRetailerExclusive;
            setProductsRetailerExclusive({...productsRetailerExclusive, ...data})
        }

    }, [productRetailerExclusive]);

    return (
        <Module {...props.moduleProps}>
            <Node className={ProductComparisonPreview.containerClassName}>
                <Node className={containerClassName}>
                    <Node className={`${ProductComparisonPreview.mainContainerClassName}__header__section`}>
                        <Node {...props.collapseButton}>
                            <Node className={ProductComparisonPreview.headerClassName}>
                                <Node className={ProductComparisonPreview.headerLabelClassName}>
                                    <Node className={`${ProductComparisonPreview.headerLabelClassName}__icon`} />
                                    <Node className={`${ProductComparisonPreview.headerLabelClassName}__main-text`}>
                                        {props.resources.productComparisonPreviewHeaderText}
                                    </Node>
                                    <Node className={`${ProductComparisonPreview.headerLabelClassName}__sub-text`}>
                                        {props.subheaderText}
                                    </Node>
                                </Node>
                            </Node>
                        </Node>
                        <Node className={ProductComparisonPreview.tableClassName} tag='table'>
                            <Node className='' tag='tbody'>
                                <Node className={ProductComparisonPreview.tableRowClassName} tag='tr'>
                                    {props.productsMainInfo?.map(info => {
                                        return (
                                            <Node className='' tag='td' key={info.productData.itemId}>
                                                {info.removeFromComparisonButton}
                                                {info.productImage}
                                            </Node>
                                        );
                                    })}
                                    {props.previewGreyPlaceHolders}
                                </Node>
                                <Node className={ProductComparisonPreview.tableRowClassName} tag='tr'>
                                    {props.productsMainInfo?.map(info => {
                                        const brandNameClassName = `${ProductComparisonPreview.tableRowClassName}__product-brand-name`;
                                        const priceNameClassName = `${ProductComparisonPreview.tableRowClassName}__product-price`;
                                        // const priceNameClassName = `${ProductComparison.productRowClassName}__product-price`;
                                        return (
                                            <Node className='' tag='td' key={info.productData.itemId}>
                                                {renderDynamicBrandName(brandNameClassName, info?.productData.product.RecordId)}
                                                {msdyn365Commerce.isBrowser ? info.productLink : undefined}
                                                {renderDynamicPrice(priceNameClassName, info?.productData.product.RecordId)}
                                            </Node>
                                        );
                                    })}
                                </Node>
                            </Node>
                        </Node>
                    </Node>
                    <Node className={ProductComparisonPreview.footerClassName}>
                        {props.clearAllButton}
                        {props.goToComparisonButton}
                    </Node>
                </Node>
            </Node>
        </Module>
    );
};

export default ProductComparisonPreviewViewFunctionalComponent;
